/**
 * Sentry initialisation — frontend (React / Next.js).
 *
 * Stage 0.2 — INFRASTRUCTURE_PLAN.md §0.2
 *
 * Import this in web/app/layout.tsx (the root layout) so it runs once
 * at application boot.  The import is safe on the server side too — when
 * NEXT_PUBLIC_SENTRY_DSN is unset the function returns early with a console
 * warning and no SDK is loaded.
 *
 * The dashboard is a Next.js static export (`output: 'export'`), so there is
 * no server-side rendering path; all Sentry captures are client-side.
 */

'use client';

import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// PII scrubber — mirrors the backend scrubber shape.
// Keeps the client-side scrubber self-contained (no shared import) to avoid
// any risk of accidentally bundling backend code into the client bundle.
// ---------------------------------------------------------------------------

const PII_PATTERNS_FRONTEND: Array<RegExp> = [
  // JWT tokens
  /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
  // Email addresses
  /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g,
  // Argentine DNI (7–8 digits, optionally dot-separated) — catches pre-1979
  // 7-digit IDs that the original regex missed.
  /\b\d{1,2}\.?\d{3}\.?\d{3}\b/g,
  // Argentine mobile in spaced/dashed user-entered forms
  /\b(?:9\s?)?(?:11|[2-9]\d{1,3})[\s\-]?\d{4}[\s\-]?\d{4}\b/g,
  // WhatsApp JIDs
  /\d{8,15}@(?:s\.whatsapp\.net|g\.us|c\.us)/g,
  // UUIDs (Supabase user IDs)
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
];

function scrubFrontendString(value: string): string {
  let out = value;
  for (const re of PII_PATTERNS_FRONTEND) {
    re.lastIndex = 0;
    out = out.replace(re, '[scrubbed]');
  }
  return out;
}

function scrubFrontendValue(value: unknown, fieldName?: string): unknown {
  if (value === null || value === undefined) return value;

  const SENSITIVE = new Set(['password', 'token', 'secret', 'key', 'email', 'tenant_name', 'tenant_dni', 'notes']);
  if (fieldName && SENSITIVE.has(fieldName.toLowerCase())) return '[scrubbed]';

  if (typeof value === 'string') return scrubFrontendString(value);
  if (Array.isArray(value)) return value.map((v) => scrubFrontendValue(v));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = scrubFrontendValue(v, k);
    }
    return out;
  }
  return value;
}

function frontendBeforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Scrub event.message — raw Sentry capture message may carry interpolated PII.
  if (event.message) {
    event.message = scrubFrontendString(event.message);
  }

  // Scrub URL — strip query string (may hold Supabase magic-link tokens) AND
  // scrub the remainder so path segments like /claims/28541234/events don't leak.
  if (event.request?.url) {
    try {
      const u = new URL(event.request.url);
      u.search = '';
      event.request.url = scrubFrontendString(u.toString());
    } catch {
      event.request.url = scrubFrontendString(event.request.url);
    }
  }

  // Scrub extra context
  if (event.extra) {
    event.extra = scrubFrontendValue(event.extra) as typeof event.extra;
  }

  // Scrub breadcrumbs
  if (event.breadcrumbs) {
    const crumbs = Array.isArray(event.breadcrumbs)
      ? event.breadcrumbs
      : (event.breadcrumbs as { values?: Sentry.Breadcrumb[] }).values ?? [];
    for (const crumb of crumbs) {
      if (crumb.message) crumb.message = scrubFrontendString(crumb.message);
      if (crumb.data) crumb.data = scrubFrontendValue(crumb.data) as typeof crumb.data;
    }
  }

  // Scrub user — keep only opaque id
  if (event.user) {
    const { id } = event.user;
    event.user = id ? { id } : {};
  }

  // Scrub exception values
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubFrontendString(ex.value);
    }
  }

  // Scrub tags / contexts / transaction — surfaced prominently in Sentry UI.
  if (event.tags) {
    event.tags = scrubFrontendValue(event.tags) as typeof event.tags;
  }
  if (event.contexts) {
    event.contexts = scrubFrontendValue(event.contexts) as typeof event.contexts;
  }
  if (event.transaction) {
    event.transaction = scrubFrontendString(event.transaction);
  }

  return event;
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

let _initialised = false;

export function initSentryFrontend(): void {
  // Guard: only initialise once and only in browser context
  if (typeof window === 'undefined') return;
  if (_initialised) return;

  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn) {
    console.warn('[sentry] Frontend Sentry disabled — NEXT_PUBLIC_SENTRY_DSN not set');
    return;
  }

  // Release SHA is baked in at build time via next.config.mjs
  const release = process.env.NEXT_PUBLIC_REPO_COMMIT ?? 'unknown';
  const environment = process.env.NODE_ENV ?? 'production';

  Sentry.init({
    dsn,
    release,
    environment,
    tracesSampleRate: 0.1,
    // replaysSessionSampleRate: 0,   // Session replay disabled — PII risk
    // replaysOnErrorSampleRate: 0,   // Disabled until security review
    beforeSend: frontendBeforeSend,
    debug: false,
    // Only report errors from our own bundle (not third-party scripts)
    allowUrls: [/ottothebutler\.com/],
  });

  _initialised = true;
  console.info(`[sentry] Frontend initialised — release=${release} env=${environment}`);
}
